import { queryOptions } from "@tanstack/react-query";
import {
  listGalleryItems,
  listGalleryItemsAdmin,
  type CaptionsStatus,
  type GalleryCollection,
} from "./gallery.functions";

export type { CaptionsStatus, GalleryCollection };

export type GalleryItem = {
  id: string;
  collection: GalleryCollection;
  title: string | null;
  description: string | null;
  permalink: string | null;
  video_path: string | null;
  poster_path: string | null;
  captions_path: string | null;
  /** Transcribe job the row is waiting on (migration 025); null before then. */
  captions_job?: string | null;
  captions_status?: CaptionsStatus | null;
  /**
   * Names, companies and products the editor expects in this video, one per
   * line (migration 026). Read by the caption correction pass and the AI
   * review; null before then or when none were entered.
   */
  captions_terms?: string | null;
  alt_text: string | null;
  duration_seconds: number | null;
  display_order: number;
  is_active: boolean | number;
  published_at: string | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
};

export const galleryItemsQueryOptions = (collection: GalleryCollection) =>
  queryOptions({
    queryKey: ["gallery_items", collection],
    queryFn: async () =>
      ((await listGalleryItems({ data: { collection } })) ?? []) as GalleryItem[],
  });

export const galleryItemsAdminQueryOptions = (collection?: GalleryCollection) =>
  queryOptions({
    queryKey: ["gallery_items", "admin", collection ?? "all"],
    queryFn: async () =>
      ((await listGalleryItemsAdmin({ data: { collection } })) ?? []) as GalleryItem[],
  });

/** What the admin table shows for a video's captions. */
export type CaptionsBadge = "none" | "generating" | "ready" | "failed";

/**
 * A present captions_path means ready (rows captioned before migration 025
 * still say 'none'); a running job wins so a regeneration shows progress.
 */
export function captionsBadge(
  item: Pick<GalleryItem, "captions_path" | "captions_status">,
): CaptionsBadge {
  const status = item.captions_status ?? "none";
  if (status === "running" || status === "queued") return "generating";
  if (item.captions_path) return "ready";
  if (status === "failed") return "failed";
  return "none";
}

/** The API already filters on is_active; this keeps the page honest if it ever doesn't. */
export function activeItems(items: GalleryItem[] | undefined): GalleryItem[] {
  return (items ?? []).filter((i) => Boolean(i.is_active));
}

export function findByPermalink(
  items: GalleryItem[] | undefined,
  permalink: string,
): GalleryItem | null {
  return activeItems(items).find((i) => i.permalink === permalink) ?? null;
}

/** 516 → "8:36", 3725 → "1:02:05", null/0 → null. */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
}

/** DATE columns arrive as "2019-05-14"; render them without a timezone shift. */
export function formatPublished(value: string | null | undefined): string | null {
  if (!value) return null;
  const iso = value.length <= 10 ? `${value}T00:00:00Z` : value;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function itemTitle(item: GalleryItem, fallback = "Untitled"): string {
  return item.title?.trim() || fallback;
}
